import { z } from 'zod';

import { FlowDefinitionState, type FlowDefinitionWithFlow } from './flowDefinition.js';
import { type BaseItem, type NullablePartial } from '../common.js';
import { oneOrMany } from '../utils.js';
import { FlowKind } from './base/baseSchema.js';
import { TagNameSchema } from './tag.js';

export { FlowKind } from './base/baseSchema.js';

export * as Admin from './admin.js';
export * as HumanTask from './humanTask/humanTask.js';

export enum FlowState {
  Active = 'Active',
  Inactive = 'Inactive',
  SoftDeleted = 'SoftDeleted',
}

export const FlowKindProps: Record<FlowKind, { name: string }> = {
  [FlowKind.Base]: { name: 'Flow' },
  [FlowKind.Skill]: { name: 'Skill' },
  [FlowKind.FlowTemplate]: { name: 'Template' },
};

export interface BaseFlow {
  name: string;
  organizationId: string;
  agentId: string | null;
  state: FlowState;
  kind: FlowKind;
  description: string | null;
  createdById: string | null;
  updatedById: string | null;
  clonedFromFlowId?: string | null;
}

export interface Flow extends BaseFlow, BaseItem {}

export interface FlowWithCreatedBy extends Flow {
  createdByEmail: string | null | undefined;
  updatedByEmail: string | null | undefined;
  agentName: string | null;
  agentCategory: string | null;
}

export interface FlowWithRunCount extends FlowWithCreatedBy {
  runCount: number;
  mostRecentRunAt?: string;
  flowDefinitionStates?: FlowDefinitionState[];
}

export type CreateValues = NullablePartial<BaseFlow>;

export type UpdateValues = Partial<Omit<CreateValues, 'createdById'>>;

export enum OrderByField {
  Name = 'name',
  State = 'state',
  Description = 'description',
  Created = 'created',
  Updated = 'updated',
  CreatedByEmail = 'createdByEmail',
  AgentName = 'agentName',
}

const OrderByFieldWithDirectionSchema = z.object({
  field: z.nativeEnum(OrderByField),
  direction: z.enum(['asc', 'desc']).default('asc'),
});

export const FlowFindAllQueryParamsSchema = z.object({
  q: z.string().optional(),
  name: z.string().optional(),
  ids: z.union([z.array(z.string().uuid()), z.string().uuid()]).optional(),
  organizationId: z.union([z.array(z.string().uuid()), z.string().uuid()]).optional(),
  agentId: z.union([z.array(z.string().uuid()), z.string().uuid()]).optional(),
  state: z.nativeEnum(FlowState).optional(),
  kind: z.nativeEnum(FlowKind).optional(),
  includeInvisible: z.coerce.boolean().default(false).optional(),
  createdBefore: z.string().date().optional(),
  createdAfter: z.string().date().optional(),
  createdByEmail: z.string().optional(),
  includeEmails: z.coerce.boolean().optional(),
  includesSubFlowDefinitionId: z.string().uuid().optional(),
  includeTags: z.coerce.boolean().default(false).optional(),
  tags: z.union([z.array(TagNameSchema), TagNameSchema]).optional(),
  includeFlowDefinitionStates: z.coerce.boolean().default(false).optional(),
  flowDefinitionStates: z.union([z.array(z.string()), z.string()]).optional(),
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
  orderBy: oneOrMany(OrderByFieldWithDirectionSchema).optional(),
});
export type FlowFindAllQueryParams = z.infer<typeof FlowFindAllQueryParamsSchema>;

export const SkillsFindAllQueryParamsSchema = z.object({
  q: z.string().optional(),
  name: z.string().optional(),
  ids: z.union([z.array(z.string().uuid()), z.string().uuid()]).optional(),
  organizationId: z.union([z.array(z.string().uuid()), z.string().uuid()]).optional(),
  agentId: z.union([z.array(z.string().uuid()), z.string().uuid()]).optional(),
  state: z.nativeEnum(FlowState).optional(),
  kind: z.nativeEnum(FlowKind).optional(),
  createdBefore: z.coerce.date().optional(),
  createdAfter: z.coerce.date().optional(),
  createdByEmail: z.string().optional(),
  includeTags: z.coerce.boolean().default(false).optional(),
  tags: z.union([z.array(TagNameSchema), TagNameSchema]).optional(),
  flowDefinitionStates: oneOrMany(z.nativeEnum(FlowDefinitionState)).optional(),
  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
  orderBy: oneOrMany(OrderByFieldWithDirectionSchema).optional(),
});
export type SkillsFindAllQueryParams = z.infer<typeof SkillsFindAllQueryParamsSchema>;

export interface FlowWithDefinitions {
  flow: FlowWithCreatedBy;
  flowDefinitions: FlowDefinitionWithFlow[];
}
